import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import PageFlyContainer from '../components/page-fly/PageFlyContainer';
import PageFlyNav from './pagefly/pagefly-navigation';
import { ShopifyProductActionsProvider } from '../contexts/ShopifyProductActionsProvider';
import { PublicApiClientProvider } from '@jbhi-fi/components/src/context/PublicApiClientProvider';
import { KlaviyoOnSiteClient } from '@jbhi-fi/apis/src/klaviyo/KlaviyoOnSiteClient';
import { KlaviyoClient } from '@jbhi-fi/apis/src/klaviyo/KlaviyoClient';
import { KlaviyoBackInStoreClient } from '@jbhi-fi/apis/src/klaviyo/KlaviyoBackInStoreClient';
import { serviceHelper } from '../config/service-helper';
import { ContentfulThemeApiClient } from '@jbhi-fi/apis/src/contentfulThemeApi';
import { ProductClientProvider } from '@jbhi-fi/components/src/context/ProductClientProvider';
import { ShopifyProductCompareProvider } from '../contexts/ShopifyProductCompareContext';
import { ShopifyCustomerProvider } from '../contexts/ShopifyCustomerProvider';
import { productClient } from '../api/algolia/ProductClient';
import { QueryClientProvider, QueryClient } from 'react-query';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { ShopifyProductProvider } from '../api/shopify/ShopifyProduct';
import { CartContextProvider } from './cart/context/CartContext';
import { HeroBannerCarousel } from '@jbhi-fi/components/src/HeroBannerCarousel/HeroBannerCarousel';
import { useContentfulAds } from '@jbhi-fi/components/src/hooks/useContentfulAds';
import { CarouselBannerTransformer } from '@jbhi-fi/components/src/Contentful/transformers/CarouselBannerTransformer';
import { WingsTransformer } from '@jbhi-fi/components/src/Contentful/transformers/WingsTransformer';
import { Wings } from '@jbhi-fi/components/src/Wings/Wings';
import { ConfigProvider } from '@jbhi-fi/components/src/context/ConfigProvider';
import { CurrentBreakpointProvider } from '@jbhi-fi/components/src/context/CurrentBreakpointProvider';

const PERKS_IDENTIFIER = '-perks';
const MAX_CAROUSEL_BANNERS = 8;
const handle = window.location.pathname.split('/').pop();
const PageFlyRedirect = () => {
  const { isPerks } = useCustomerContext();
  useEffect(() => {
    if (isPerks && window.perksRedirect) {
      window.location.pathname = window.location.pathname + PERKS_IDENTIFIER;
    } else if (!isPerks && window.location.pathname.endsWith(PERKS_IDENTIFIER)) {
      window.location.pathname = window.location.pathname.replace(PERKS_IDENTIFIER, '');
    }
  }, [isPerks]);
  return null;
};
const contentfulThemeApiClient = new ContentfulThemeApiClient({ themeApiBaseUrl: serviceHelper.themeApisUrl });
const PageFly = () => {
  if (document.getElementById('__pf')) {
    PageFlyNav();
  }
  //PNP-1873-Grab nodes where the attribute 'data-pagefly-sku' exists
  const pageFlyNodes = document.querySelectorAll('[data-pagefly-sku]');
  // PNP-1873-Create css classes using attributes as opposed to css styles because pagefly styles are dynamic.
  if (pageFlyNodes && pageFlyNodes[0]) {
    pageFlyNodes[0].closest('[data-pf-type="Row"]').dataset.pageflyRow = 'true';
    pageFlyNodes[0].closest('[data-pf-type="Column"]').dataset.pageflyColumn = 'true';
  }
  // PNP-1873-Create variable 'node' that passes through PageFlyNodes array and renders using parent container PageFlyContainer.js
  const node = pageFlyNodes[0];

  const klaviyoOnSiteClient = new KlaviyoOnSiteClient({
    klaviyoEnvironment: window.Jbhifi.klaviyoAccount,
    klaviyoSubscribeUrl: process.env.PUBLIC_KLAVIYO_NOTIFY_ME_URL || '',
    klaviyoNotifyMeListId: window.Jbhifi.klaviyoMailingListId,
  });
  const klaviyoClient = new KlaviyoClient({
    klaviyoEnvironment: window.Jbhifi.klaviyoAccount,
    klaviyoSubscribeUrl: process.env.PUBLIC_KLAVIYO_SUBSCRIBE_URL || '',
    countryCode: window.Jbhifi.storeLocation,
  });
  const klaviyoBackInStoreClient = new KlaviyoBackInStoreClient({
    klaviyoEnvironment: window.Jbhifi.klaviyoAccount,
    klaviyoBackInStoreUrl: process.env.PUBLIC_KLAVIYO_BACK_IN_STOCK_SUBSCRIPTIONS_URL || '',
  });
  const queryClient = new QueryClient();

  const PageFlyAds = () => {
    const { banners, isLoading, wings } = useContentfulAds({
      queryOptions: { handle: handle },
      contentfulThemeApiClient,
      isPagefly: true,
    });
    const bannerElement = document.getElementById('pagefly-banner-carousel');
    const wingsElement = document.getElementsByClassName('page-width')[0];
    let wingsPortal = null;

    if (wings && wingsElement) {
      const wingsContainer = document.createElement('div');
      wingsElement.prepend(wingsContainer);
      wingsPortal = createPortal(<Wings {...WingsTransformer.toWingsProps(wings)} />, wingsContainer);
    }
    return (
      <>
        {wingsPortal}
        {banners?.length && bannerElement
          ? createPortal(
              <HeroBannerCarousel
                {...CarouselBannerTransformer.toHeroBannerCarouselProps(banners.slice(0, MAX_CAROUSEL_BANNERS), {
                  variant: 'collection',
                })}
                isLoading={isLoading}
                variant="collection"
              />,
              bannerElement
            )
          : null}
      </>
    );
  };

  const bodyContainer = document.getElementById('page-body-container');
  if (bodyContainer && window.location.pathname.includes('/pages/')) {
    const mountPoint = document.createElement('div');
    bodyContainer.appendChild(mountPoint);
    const root = createRoot(mountPoint);
    root.render(
      <QueryClientProvider client={queryClient}>
        <ProductClientProvider client={productClient}>
          <CartContextProvider>
            <ShopifyCustomerProvider>
              <CurrentBreakpointProvider>
                <PageFlyRedirect />
                <PageFlyAds />
              </CurrentBreakpointProvider>
            </ShopifyCustomerProvider>
          </CartContextProvider>
        </ProductClientProvider>
      </QueryClientProvider>
    );
  }
  if (node) {
    const root = createRoot(node);
    root.render(
      <ConfigProvider contentfulImageQuality={window.Jbhifi.contentfulSettings?.defaultContentfulImageQuality}>
        <QueryClientProvider client={queryClient}>
          <ProductClientProvider client={productClient}>
            <PublicApiClientProvider
              klaviyoClient={klaviyoClient}
              klaviyoBackInStoreClient={klaviyoBackInStoreClient}
              klaviyoOnSiteClient={klaviyoOnSiteClient}
              contentfulThemeApiClient={contentfulThemeApiClient}
            >
              <CartContextProvider>
                <ShopifyCustomerProvider>
                  <ShopifyProductCompareProvider>
                    <ShopifyProductProvider>
                      <ShopifyProductActionsProvider attachPageType="pagefly">
                        <CurrentBreakpointProvider>
                          <PageFlyRedirect />
                          <PageFlyContainer skuNodes={Array.from(pageFlyNodes)} />
                        </CurrentBreakpointProvider>
                      </ShopifyProductActionsProvider>
                    </ShopifyProductProvider>
                  </ShopifyProductCompareProvider>
                </ShopifyCustomerProvider>
              </CartContextProvider>
            </PublicApiClientProvider>
          </ProductClientProvider>
        </QueryClientProvider>
      </ConfigProvider>,
      node
    );
  }
};

export default PageFly;
